import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface ThreadMessage {
  direction: 'guest' | 'bot' | 'agent';
  content: string;
  sentAt: string;
}

interface ThreadResponse {
  taskId: string;
  chatId: string | null;
  available: boolean;
  messages: ThreadMessage[];
}

interface Props {
  taskId: string;
}

function isFromGuest(message: ThreadMessage): boolean {
  return message.direction === 'guest';
}

function messageLabel(message: ThreadMessage): string {
  if (message.direction === 'agent') return 'Operatör';
  if (message.direction === 'bot') return 'AI';
  return 'Misafir';
}

function messageTime(message: ThreadMessage): string {
  const parsed = new Date(message.sentAt);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleString();
}

export default function ChatTaskThread({ taskId }: Props) {
  const navigate = useNavigate();
  const [thread, setThread] = useState<ThreadResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const loadThread = useCallback(async () => {
    try {
      const response = await api.get(`/service-tasks/${taskId}/chat-thread`);
      setThread(response.data);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Konuşma yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  useEffect(() => {
    const interval = setInterval(() => loadThread(), 10000);
    return () => clearInterval(interval);
  }, [loadThread]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatIcon fontSize="small" sx={{ color: '#189BF0' }} />
          <Typography variant="subtitle2">Misafir sohbeti</Typography>
        </Box>
        {thread?.chatId && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/dashboard/chat/${thread.chatId}`)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Sohbete git
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {thread && !thread.available && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Bu görev için sohbet kaydı bulunamadı.
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{ maxHeight: 320, overflowY: 'auto', p: 1.5, mb: 1, bgcolor: 'background.default' }}
      >
        {thread?.messages?.length ? (
          thread.messages.map((message, index) => {
            const fromGuest = isFromGuest(message);
            return (
              <Box
                key={`${message.sentAt}-${index}`}
                sx={{ display: 'flex', justifyContent: fromGuest ? 'flex-start' : 'flex-end', mb: 1 }}
              >
                <Box
                  sx={{
                    maxWidth: '80%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: fromGuest
                      ? 'grey.200'
                      : message.direction === 'bot'
                        ? 'rgba(24, 155, 240, 0.15)'
                        : '#DCF8C6',
                    color: 'text.primary',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content.replace(/<[^>]+>/g, '')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[messageLabel(message), messageTime(message)].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Box>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            Henüz mesaj yok.
          </Typography>
        )}
        <div ref={bottomRef} />
      </Paper>
    </Box>
  );
}
